import React, { useState } from "react";
import { useGetFormativeAssessment } from "@packages/api/madlenAPI";
import { Expand, X, ClipboardList } from "lucide-react";
import Text from "@core/text";
import { Button } from "@core/button";
import { FormativeAssessmentResponseDto } from "@packages/api/madlenAPI.schemas";
import FormativeAssessmentCard from "@features/tools/components/formative-assessment/FormativeAssessmentCard";
import Markdown from "react-markdown";
import { useTranslation } from "react-i18next";
import SpinnerLoader from "../components/SpinnerLoader";

interface FormativeAssessmentProps {
  id: number | null;
}

const FormativeAssessmentDetailComponent = ({
  project,
  closeModal,
}: {
  project: FormativeAssessmentResponseDto;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-[9999] overflow-auto bg-black bg-opacity-75 dark:bg-opacity-90 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeModal();
        }
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <Text variant={"h1"}>
            {project.title || t("formativeAssessment.modalTitle")}
          </Text>
          <Button
            onClick={closeModal}
            variant="solid"
            size="icon"
            rounded="full"
            bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={24} className="text-gray-500 dark:text-gray-400" />
          </Button>
        </div>

        <div className="flex-1 overflow-auto p-6 modal-content">
          {project && <FormativeAssessmentCard assessment={project} />}
        </div>
      </div>
    </div>
  );
};

const FormativeAssessment: React.FC<FormativeAssessmentProps> = ({ id }) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  const { data, isLoading, isError } = useGetFormativeAssessment(id, {
    query: {
      refetchInterval: (data) => {
        if (
          data?.state.data?.studentReflection &&
          data.state.data.studentReflection?.length > 0
        ) {
          return false;
        }
        return 2000;
      },
      enabled: !!id,
      staleTime: Infinity,
      cacheTime: 1000 * 60 * 30,
    },
  });

  const isContentLoading =
    isLoading ||
    !data?.studentReflection ||
    data.studentReflection.length === 0;

  if (isContentLoading) {
    return <SpinnerLoader />;
  }

  if (isError) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <h3 className="text-xl font-medium mb-4 text-red-500 dark:text-red-400">
          {t("formativeAssessment.error.unableToLoad")}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("formativeAssessment.error.tryRegenerate")}
        </p>
      </div>
    );
  }

  const handleExpandClick = () => {
    setExpanded(true);
  };

  const closeModal = () => {
    setExpanded(false);
  };

  return (
    <>
      {expanded && data && (
        <FormativeAssessmentDetailComponent
          project={{
            ...data,
            gradeLevel: data.gradeLevel === -1 ? 0 : data.gradeLevel,
          }}
          closeModal={closeModal}
        />
      )}

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-medium flex items-center text-gray-900 dark:text-gray-100">
            {t("formativeAssessment.title")}
          </h3>
          <button
            className="flex items-center justify-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-200"
            onClick={handleExpandClick}
          >
            <Expand size={18} className="text-gray-400 dark:text-gray-500" />
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("formativeAssessment.subtitle")}
        </p>

        {data && (
          <div className="space-y-8">
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-gray-200/50 dark:border-gray-700/50">
              <div className="space-y-8">
                {/* Process Section */}
                {data.process && (
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-xl bg-yellow-100 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-400">
                        <ClipboardList size={20} />
                      </div>
                      <h4 className="font-semibold text-yellow-600 dark:text-yellow-400">
                        {t("formativeAssessment.process")}
                      </h4>
                    </div>
                    <div className="leading-relaxed text-gray-800 dark:text-gray-200">
                      <Markdown className="prose dark:prose-invert">
                        {data.process}
                      </Markdown>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FormativeAssessment;
